import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Cursor follower: a subtle blob + dot that trails the mouse.
 * Enlarges over interactive elements. Hidden on touch devices and when reduced-motion.
 */
export default function CursorFollower() {
  const [enabled, setEnabled] = useState(false);
  const [hover, setHover] = useState(false);
  const [label, setLabel] = useState(null);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);

  const sx = useSpring(x, { damping: 30, stiffness: 300, mass: 0.4 });
  const sy = useSpring(y, { damping: 30, stiffness: 300, mass: 0.4 });

  useEffect(() => {
    const isTouch = window.matchMedia("(hover: none)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouch || reduce) return;

    setEnabled(true);
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    const over = (e) => {
      const t = e.target.closest(
        "a, button, [role='button'], input, textarea, select, [data-cursor]"
      );
      if (t) {
        setHover(true);
        setLabel(t.getAttribute("data-cursor"));
      } else {
        setHover(false);
        setLabel(null);
      }
    };
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <>
      <motion.div
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[200] rounded-full mix-blend-difference"
        style={{
          x: sx,
          y: sy,
          translateX: "-50%",
          translateY: "-50%",
          width: hover ? 48 : 22,
          height: hover ? 48 : 22,
          background: "#E2FF31",
          opacity: hover ? 0.95 : 0.5,
          transition: "width 0.25s ease, height 0.25s ease, opacity 0.25s ease",
        }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[201]"
        style={{
          x,
          y,
          translateX: "-50%",
          translateY: "-50%",
        }}
      >
        <div
          className={`w-1.5 h-1.5 rounded-full bg-white ${
            hover ? "opacity-0" : "opacity-100"
          } transition-opacity`}
        />
      </motion.div>
      {label && (
        <motion.div
          aria-hidden
          className="pointer-events-none fixed top-0 left-0 z-[202] font-mono-tag text-[10px] text-black bg-[#E2FF31] px-2 py-1 rounded-full"
          style={{
            x: sx,
            y: sy,
            translateX: 22,
            translateY: -8,
          }}
        >
          {label}
        </motion.div>
      )}
    </>
  );
}
